/**
 * Care team notes: dated, internal notes on the chart (dose changes, side
 * effects, follow-ups, pending requests). Pinned notes sit at the top of the
 * prescriber's chart drawer and Prescribing review, so what the nurse knows is
 * in front of Dr. Hinds when he reviews. Never shown to the patient.
 *
 * A dose-change note can be sent for sign-off: it opens a dose review in the
 * prescriber's worklist (the dose-change panel) and holds future fills of that
 * treatment until it is answered, so a dose agreed by phone or in chat reaches
 * the prescription instead of only the conversation.
 */
import { useState } from "react";

import { Badge, Button } from "@/design-system/primitives";
import {
  useCareTeamNotes,
  useCreateCareTeamNote,
  useUpdateCareTeamNote,
  type NotesSurface,
} from "@/lib/api/careTeamNotes";
import type { CareTeamNote, CareTeamNoteKind } from "@/types/careTeamNotes";

export const KIND_LABEL: Record<CareTeamNoteKind, string> = {
  note: "Note",
  dose_change: "Dose change",
  side_effect: "Side effect / reaction",
  follow_up: "Follow-up",
  pending_request: "Pending request",
};

const KINDS = Object.keys(KIND_LABEL) as CareTeamNoteKind[];

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function CareTeamNotes({
  patientId,
  surface = "clinician",
}: {
  patientId: string;
  surface?: NotesSurface;
}) {
  const notes = useCareTeamNotes(patientId, surface);
  const [adding, setAdding] = useState(false);
  const [showAll, setShowAll] = useState(false);

  if (notes.isLoading) return null;
  if (notes.isError || !notes.data) {
    return (
      <p className="type-body-sm text-danger">Care team notes could not be loaded.</p>
    );
  }
  const { notes: all, treatments, signoff_enabled } = notes.data;
  const label = (family: string | null) =>
    treatments.find((t) => t.family === family)?.label ?? family ?? null;
  const pinned = all.filter((note) => note.pinned && !note.resolved_at);

  return (
    <section
      className="border border-line rounded p-12 flex flex-col gap-10"
      aria-label="Care team notes"
    >
      <div className="flex flex-wrap items-center justify-between gap-8">
        <h3 className="type-label-md font-semibold">
          Care team notes{all.length ? ` (${all.length})` : ""}
        </h3>
        <div className="flex gap-6">
          {all.length > 0 && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowAll((value) => !value)}
            >
              {showAll ? "Hide timeline" : "Timeline"}
            </Button>
          )}
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => setAdding((value) => !value)}
          >
            {adding ? "Cancel" : "Add note"}
          </Button>
        </div>
      </div>

      {adding && (
        <NoteForm
          patientId={patientId}
          surface={surface}
          treatments={treatments}
          signoffEnabled={signoff_enabled}
          onDone={() => setAdding(false)}
        />
      )}

      {pinned.length > 0 && !showAll && (
        <ul className="flex flex-col gap-6" aria-label="Pinned notes">
          {pinned.map((note) => (
            <NoteRow
              key={note.id}
              note={note}
              treatment={label(note.product_family)}
              patientId={patientId}
              surface={surface}
              highlight
            />
          ))}
        </ul>
      )}
      {pinned.length === 0 && !showAll && !adding && (
        <p className="type-body-sm text-ink-muted">
          {all.length
            ? "No pinned notes. Open the timeline for the full history."
            : "No notes yet. Pin anything the prescriber should see during review."}
        </p>
      )}

      {showAll && (
        // One dated sequence: every note, pinned ones kept in place and marked.
        <ul className="flex flex-col gap-6" aria-label="Notes timeline">
          {[...all]
            .sort((a, b) => b.created_at.localeCompare(a.created_at))
            .map((note) => (
              <NoteRow
                key={note.id}
                note={note}
                treatment={label(note.product_family)}
                patientId={patientId}
                surface={surface}
                highlight={note.pinned && !note.resolved_at}
              />
            ))}
        </ul>
      )}
    </section>
  );
}

function NoteRow({
  note,
  treatment,
  patientId,
  surface,
  highlight = false,
}: {
  note: CareTeamNote;
  treatment: string | null;
  patientId: string;
  surface: NotesSurface;
  highlight?: boolean;
}) {
  const update = useUpdateCareTeamNote(patientId, surface);
  const done = Boolean(note.resolved_at);
  // A prescriber's answer, or a clinician clearing the request, closes a dose review.
  const cleared = note.signoff_status === "withdrawn";
  const noChange = note.signoff_status === "no_change";
  const awaiting =
    Boolean(note.dose_change_request_id) &&
    note.signoff_status !== "resolved" &&
    !cleared &&
    !noChange;
  return (
    <li
      className={`rounded p-10 flex flex-col gap-6 border border-line-subtle ${
        highlight ? "border-l-[3px] border-l-warning bg-page" : ""
      } ${done ? "opacity-70" : ""}`}
    >
      <div className="flex flex-wrap items-center gap-6 type-label-xs text-ink-muted">
        <span>{formatDate(note.created_at)}</span>
        <Badge tone={note.kind === "side_effect" ? "danger" : "neutral"} size="sm">
          {KIND_LABEL[note.kind]}
        </Badge>
        {treatment && <span>{treatment}</span>}
        {note.author_name && <span>· {note.author_name}</span>}
        {note.dose_change_request_id && (
          <Badge tone={awaiting ? "warning" : "success"} size="sm">
            {awaiting
              ? "Awaiting prescriber"
              : cleared
                ? "Cleared"
                : noChange
                  ? "Closed, no dose change"
                  : "Signed off"}
          </Badge>
        )}
        {done && (
          <Badge tone="success" size="sm">
            Done{note.resolved_by_name ? ` · ${note.resolved_by_name}` : ""}
          </Badge>
        )}
      </div>
      <p className="type-body-sm whitespace-pre-wrap break-words">{note.body}</p>
      <div className="flex gap-6">
        {!done && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={update.isPending}
            onClick={() =>
              update.mutate({ noteId: note.id, patch: { pinned: !note.pinned } })
            }
          >
            {note.pinned ? "Unpin" : "Pin"}
          </Button>
        )}
        {awaiting && !done ? (
          <span className="text-12 text-ink-muted self-center">
            Closes when the prescriber answers it in the worklist.
          </span>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={update.isPending}
            onClick={() => update.mutate({ noteId: note.id, patch: { resolved: !done } })}
          >
            {done ? "Reopen" : "Mark done"}
          </Button>
        )}
      </div>
    </li>
  );
}

function NoteForm({
  patientId,
  surface,
  treatments,
  signoffEnabled,
  onDone,
}: {
  patientId: string;
  surface: NotesSurface;
  treatments: { family: string; label: string }[];
  signoffEnabled: boolean;
  onDone: () => void;
}) {
  const create = useCreateCareTeamNote(patientId, surface);
  // One id per form: a retry after a failed save completes the same note.
  const [clientId] = useState(() => crypto.randomUUID());
  const [kind, setKind] = useState<CareTeamNoteKind>("note");
  const [family, setFamily] = useState("");
  const [body, setBody] = useState("");
  const [pinned, setPinned] = useState(true);
  const [signoff, setSignoff] = useState(false);
  const canSignoff = signoffEnabled && kind === "dose_change";
  const wantsSignoff = canSignoff && signoff;
  const missingTreatment = wantsSignoff && !family;

  return (
    <form
      className="border border-line-subtle rounded p-10 flex flex-col gap-8"
      onSubmit={(event) => {
        event.preventDefault();
        if (!body.trim() || missingTreatment) return;
        create.mutate(
          {
            kind,
            body: body.trim(),
            pinned,
            product_family: family || null,
            send_for_signoff: wantsSignoff,
            client_id: clientId,
          },
          { onSuccess: onDone },
        );
      }}
    >
      <div className="grid sm:grid-cols-2 gap-8">
        <label className="flex flex-col gap-4">
          <span className="type-label-sm font-semibold">Type</span>
          <select
            value={kind}
            onChange={(event) => setKind(event.target.value as CareTeamNoteKind)}
            className="border border-line rounded px-8 py-6 bg-page type-body-sm"
          >
            {KINDS.map((value) => (
              <option key={value} value={value}>
                {KIND_LABEL[value]}
              </option>
            ))}
          </select>
        </label>
        <label className="flex flex-col gap-4">
          <span className="type-label-sm font-semibold">
            Treatment{wantsSignoff ? "" : " (optional)"}
          </span>
          <select
            value={family}
            onChange={(event) => setFamily(event.target.value)}
            className="border border-line rounded px-8 py-6 bg-page type-body-sm"
          >
            <option value="">{wantsSignoff ? "Choose a treatment" : "Not specific"}</option>
            {treatments.map((t) => (
              <option key={t.family} value={t.family}>
                {t.label}
              </option>
            ))}
          </select>
        </label>
      </div>
      <label className="flex flex-col gap-4">
        <span className="type-label-sm font-semibold">Note</span>
        <textarea
          rows={3}
          value={body}
          maxLength={4000}
          onChange={(event) => setBody(event.target.value)}
          placeholder={
            kind === "dose_change"
              ? "e.g. Prescriber approved 7.5 mg weekly by phone 9/15; directions on file still say 5 mg."
              : "What should the care team and prescriber know?"
          }
          className="border border-line rounded p-8 type-body-sm"
        />
      </label>
      <label className="flex items-center gap-6 type-body-sm">
        <input
          type="checkbox"
          checked={pinned}
          onChange={(event) => setPinned(event.target.checked)}
        />
        Pin to the top of the prescriber's review
      </label>
      {canSignoff && (
        <label className="flex items-start gap-6 type-body-sm">
          <input
            type="checkbox"
            checked={signoff}
            onChange={(event) => setSignoff(event.target.checked)}
            className="mt-3"
          />
          <span>
            Send to the prescriber for sign-off
            <span className="block text-12 text-ink-muted">
              Opens a dose review in the prescriber's worklist and holds future fills of
              this treatment until they answer. Their answer updates the prescription.
            </span>
          </span>
        </label>
      )}
      {create.isError && (
        <p className="type-body-sm text-danger" role="alert">
          {create.error instanceof Error ? create.error.message : "The note could not be saved."}
        </p>
      )}
      <div>
        <Button
          type="submit"
          variant="primary"
          size="sm"
          disabled={create.isPending || !body.trim() || missingTreatment}
        >
          {wantsSignoff ? "Save and send for sign-off" : "Save note"}
        </Button>
      </div>
    </form>
  );
}
